/* ==========================================================================
   My Teacher Says — Build a Bundle picker

   Deferred, dependency-free. With JavaScript off the page still reads as a catalogue — every
   stamp shows with its photograph and its name — but nothing on it is actionable. The per-card
   Details links that used to offer a way through to each product were removed (Liam, 30 Aug)
   because they sat on a card where every other tap adds to the bundle, so a mis-tap navigated
   the visitor off the page.

   No currency is formatted here. Every money string is rendered by Liquid's money filter and
   swapped in from a data attribute, so a change of currency or money_format needs no JS change.

   The picker adds six real variants to the basket. The 12.5% comes off via a Shopify automatic
   discount restricted to the eligible collection, which is why nothing here writes a price.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-mts-bundle]');
  if (!root) return;

  var SIZE = parseInt(root.dataset.size, 10) || 6;
  var STORE_KEY = 'mts-bundle-v1';

  var grid = root.querySelector('[data-bb-grid]');
  var tray = root.querySelector('[data-bb-tray]');
  if (!grid || !tray) return;

  var slots = Array.prototype.slice.call(root.querySelectorAll('[data-bb-slot]'));
  var addBtn = root.querySelector('[data-bb-add]');
  var countEl = root.querySelector('[data-bb-count]');
  var hintEl = root.querySelector('[data-bb-hint]');
  var savingEl = root.querySelector('[data-bb-saving]');
  var progressEl = root.querySelector('[data-bb-progress]');
  var liveEl = root.querySelector('[data-bb-live]');
  var toastEl = root.querySelector('[data-bb-toast]');
  var handle = root.querySelector('[data-bb-handle]');
  var noResults = root.querySelector('[data-bb-noresults]');

  var cards = Array.prototype.slice.call(root.querySelectorAll('[data-bb-card]'));

  var palette = parseJSON(tray.dataset.palette, []);
  var defaultInk = pickDefaultInk(palette);

  /* Chosen stamps, in the order they were picked. Each entry:
     { id, title, image, variants: {Colour: variantId}, ink: 'Black' } */
  var chosen = [];

  /* Tracks the transition into a complete bundle, so the button is brought into view once
     rather than on every re-render. */
  var wasComplete = false;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function nudgeIntoView(el) {
    if (!el || !el.getBoundingClientRect) return;

    var box = el.getBoundingClientRect();
    if (box.top >= 0 && box.bottom <= window.innerHeight) return;

    el.scrollIntoView({
      block: 'nearest',
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }

  /* ----------------------------------------------------------------------
     Card lookup by product id, so restoring a saved bundle and toggling a
     card off from its tray slot both find the same element.
     ---------------------------------------------------------------------- */
  var byId = {};
  cards.forEach(function (card) {
    byId[card.dataset.id] = card;
  });

  function parseJSON(raw, fallback) {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  /* No longer a default for adding — choosing the colour is what adds the stamp. This is only
     the fallback for a bundle restored from storage whose saved ink is missing or unrecognised. */
  function pickDefaultInk(list) {
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].name).toLowerCase() === 'black') return list[i].name;
    }
    return list.length ? list[0].name : '';
  }

  function indexOfId(id) {
    for (var i = 0; i < chosen.length; i++) {
      if (chosen[i].id === id) return i;
    }
    return -1;
  }

  /* ----------------------------------------------------------------------
     Selection
     ---------------------------------------------------------------------- */
  /* The ink is passed in rather than defaulted: choosing the colour is what adds the stamp, so
     there is no such thing as an add without one. */
  function add(card, ink) {
    if (chosen.length >= SIZE) {
      full(card);
      return;
    }

    chosen.push({
      id: card.dataset.id,
      title: card.dataset.title,
      image: card.dataset.image,
      variants: parseJSON(card.dataset.variants, {}),
      ink: ink
    });

    card.classList.add('is-chosen');
    announce(card.dataset.title + ' in ' + ink + ' added — ' + chosen.length + ' of ' + SIZE);
    render();
  }

  function remove(id) {
    var i = indexOfId(id);
    if (i === -1) return;

    var title = chosen[i].title;
    chosen.splice(i, 1);

    var card = byId[id];
    if (card) {
      card.classList.remove('is-chosen');
    }

    announce(title + ' removed');
    render();
  }

  /* A seventh pick is refused in the card itself — never a browser dialog, and never silently. */
  function full(card) {
    card.classList.add('is-full');
    announce("That's six already — remove one to swap it out.");
    window.setTimeout(function () {
      card.classList.remove('is-full');
    }, 1600);
  }

  function setInk(id, name) {
    var i = indexOfId(id);
    if (i === -1) return;
    chosen[i].ink = name;
    announce(chosen[i].title + ' in ' + name);
    render();
  }

  /* ----------------------------------------------------------------------
     Rendering — slots, counters, button state
     ---------------------------------------------------------------------- */
  function render() {
    slots.forEach(function (slot, i) {
      var pick = chosen[i];

      if (!pick) {
        slot.className = 'mts-bb__slot';
        slot.innerHTML =
          '<span class="mts-bb__slot-num">' + (i + 1) + '</span>' +
          '<span class="mts-bb__slot-empty">Empty</span>';
        return;
      }

      slot.className = 'mts-bb__slot is-filled';

      slot.innerHTML =
        '<img class="mts-bb__slot-img" src="' + escapeAttr(pick.image) + '" alt="" width="56" height="56" loading="lazy">' +
        '<span class="mts-bb__slot-body">' +
        '<span class="mts-bb__slot-name">' + escapeHTML(pick.title) + '</span>' +
        /* Six coloured dots do not say "ink" to anyone looking at them, and the selected one was
           distinguishable only by a thin ring. Naming it in words does both jobs at once. */
        '<span class="mts-bb__inklabel">Ink colour: <b>' + escapeHTML(pick.ink) + '</b></span>' +
        '</span>' +
        '<button type="button" class="mts-bb__drop" data-bb-drop aria-label="Remove ' +
        escapeAttr(pick.title) + ' from your bundle">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false">' +
        '<path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"></path></svg></button>';
    });

    /* Each card's swatches carry their own selected state, and the label under the name says
       in words which ink the stamp was added in. */
    cards.forEach(function (card) {
      var i = indexOfId(card.dataset.id);
      var ink = i === -1 ? null : chosen[i].ink;

      var label = card.querySelector('[data-bb-addlabel]');
      if (label) label.textContent = ink ? 'Added in ' + ink : 'Choose an ink to add';

      card.querySelectorAll('[data-bb-ink]').forEach(function (swatch) {
        var on = swatch.dataset.bbInk === ink;
        swatch.classList.toggle('is-on', on);
        swatch.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });

    var n = chosen.length;
    var left = SIZE - n;
    var done = n === SIZE;

    if (countEl) countEl.textContent = n + ' of ' + SIZE;
    if (hintEl) {
      hintEl.textContent = done
        ? 'Bundle complete'
        : n === 0
          ? 'Choose your first stamp'
          : left + ' to go';
    }

    if (progressEl) progressEl.style.width = (n / SIZE) * 100 + '%';
    root.classList.toggle('is-complete', done);

    /* The first screenful of the grid holds more than six stamps, so a bundle can be completed
       without scrolling at all — and on a desktop rail that has not yet stuck to the top of the
       viewport, the basket button is then below the fold. Nobody hunts for a button they have no
       reason to believe exists. Only on the transition into completeness, only when the button is
       actually out of sight, and only far enough to bring it in. */
    if (done && !wasComplete) nudgeIntoView(addBtn);
    wasComplete = done;

    if (savingEl) {
      savingEl.textContent = done ? savingEl.dataset.complete : savingEl.dataset.partial;
    }

    if (addBtn) {
      addBtn.disabled = !done;
      addBtn.textContent = done
        ? addBtn.dataset.labelComplete
        : 'Choose ' + left + ' more';
    }

    save();
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeAttr(s) {
    return escapeHTML(s);
  }

  /* ----------------------------------------------------------------------
     Feedback — a status line for screen readers, a toast for everyone else.
     ---------------------------------------------------------------------- */
  var toastTimer;
  function announce(message) {
    if (liveEl) liveEl.textContent = message;
    if (!toastEl) return;

    toastEl.textContent = message;
    toastEl.hidden = false;
    /* Reading offsetWidth forces the style to settle so the transition runs on a re-shown
       toast rather than jumping straight to its end state. */
    void toastEl.offsetWidth;
    toastEl.classList.add('is-up');

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toastEl.classList.remove('is-up');
    }, 1800);
  }

  /* ----------------------------------------------------------------------
     Persistence — six choices is real work, and losing them to a stray tap on
     a product link would be the fastest way to lose the sale. Session-scoped,
     so it does not outlive the visit.
     ---------------------------------------------------------------------- */
  function save() {
    try {
      window.sessionStorage.setItem(
        STORE_KEY,
        JSON.stringify(
          chosen.map(function (c) {
            return { id: c.id, ink: c.ink };
          })
        )
      );
    } catch (e) {
      /* Private browsing, or storage full. The picker still works for this pageview. */
    }
  }

  function restore() {
    var saved;
    try {
      saved = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '[]');
    } catch (e) {
      return;
    }
    if (!saved || !saved.length) return;

    saved.slice(0, SIZE).forEach(function (entry) {
      var card = byId[entry.id];
      /* A product sold out or left the collection since the bundle was saved — skip it
         rather than restoring a stamp that can no longer be bought. */
      if (!card) return;

      chosen.push({
        id: card.dataset.id,
        title: card.dataset.title,
        image: card.dataset.image,
        variants: parseJSON(card.dataset.variants, {}),
        ink: entry.ink || defaultInk
      });

      card.classList.add('is-chosen');
    });
  }

  /* ----------------------------------------------------------------------
     Events
     ---------------------------------------------------------------------- */
  grid.addEventListener('click', function (event) {
    var swatch = event.target.closest('[data-bb-ink]');
    if (!swatch) return;

    var card = swatch.closest('[data-bb-card]');
    if (!card) return;

    var ink = swatch.dataset.bbInk;
    var i = indexOfId(card.dataset.id);

    if (i === -1) {
      add(card, ink);
    } else if (chosen[i].ink === ink) {
      /* Tapping the colour it is already in is the way back out. */
      remove(card.dataset.id);
    } else {
      setInk(card.dataset.id, ink);
    }
  });

  tray.addEventListener('click', function (event) {
    var slot = event.target.closest('[data-bb-slot]');
    if (!slot) return;

    var index = parseInt(slot.dataset.slot, 10);
    var pick = chosen[index];
    if (!pick) return;

    if (event.target.closest('[data-bb-drop]')) remove(pick.id);
  });

  /* Subject chips. Filtering only hides cards — a chosen stamp stays chosen while hidden,
     so switching subject mid-build never costs a pick. */
  var chips = Array.prototype.slice.call(root.querySelectorAll('[data-bb-filter]'));

  /* A chip matching nothing in the catalogue is a dead end, so it never renders. */
  chips.forEach(function (chip) {
    var tag = chip.dataset.bbFilter;

    /* Liquid counts the collection; the grid drops anything personalised or off the unit price,
       so the two can disagree. Counting the cards that actually rendered keeps the label honest
       without a second pass over the products. */
    if (tag === 'all') {
      chip.textContent = 'All ' + cards.length;
      return;
    }

    var hits = cards.filter(function (card) {
      return card.dataset.tags.indexOf(tag) !== -1;
    });

    if (!hits.length) chip.remove();
  });

  root.addEventListener('click', function (event) {
    var chip = event.target.closest('[data-bb-filter]');
    if (!chip) return;

    var tag = chip.dataset.bbFilter;
    var shown = 0;

    root.querySelectorAll('[data-bb-filter]').forEach(function (other) {
      var on = other === chip;
      other.classList.toggle('is-active', on);
      other.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    cards.forEach(function (card) {
      var match = tag === 'all' || card.dataset.tags.indexOf(tag) !== -1;
      card.hidden = !match;
      if (match) shown++;
    });

    if (noResults) noResults.hidden = shown > 0;
  });

  /* The tray is a bottom sheet below 1000px; the handle opens it. Above that it is a
     sticky rail and the handle is not rendered at all by CSS. */
  if (handle) {
    handle.addEventListener('click', function () {
      var open = root.classList.toggle('is-open');
      handle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ----------------------------------------------------------------------
     The tray belongs to the picker, so it retires with it. Below 1000px it is
     fixed to the bottom of the viewport, and left alone it would sit over the
     contact panel and the footer for the whole of the rest of the page.

     Failing open is the safe direction: without IntersectionObserver the tray
     simply stays put, which costs a little of the footer rather than hiding
     the basket button.
     ---------------------------------------------------------------------- */
  var picker = root.querySelector('.mts-bb__picker');
  if (picker && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      function (entries) {
        root.classList.toggle('is-tray-out', !entries[0].isIntersecting);
      },
      { rootMargin: '0px 0px -40px 0px' }
    ).observe(picker);
  }

  /* ----------------------------------------------------------------------
     Add to basket — six real line items. The discount is Shopify's.
     ---------------------------------------------------------------------- */
  if (addBtn) {
    addBtn.addEventListener('click', function () {
      if (chosen.length !== SIZE || addBtn.disabled) return;

      var items = [];
      var missing = [];

      chosen.forEach(function (pick) {
        var id = pick.variants[pick.ink];
        if (id) {
          items.push({ id: id, quantity: 1 });
        } else {
          missing.push(pick.title + ' in ' + pick.ink);
        }
      });

      /* A colour that has no variant on that product would otherwise fail silently at the
         cart endpoint with the customer none the wiser. */
      if (missing.length) {
        announce('Sorry — ' + missing[0] + " isn't available. Try another colour.");
        return;
      }

      addBtn.disabled = true;
      addBtn.classList.add('is-busy');

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart');
          return response.json();
        })
        .then(function () {
          /* The basket is where the discount becomes visible, and seeing £42 confirmed is
             the reassurance the whole page has been promising. */
          try {
            window.sessionStorage.removeItem(STORE_KEY);
          } catch (e) {
            /* Nothing to clean up. */
          }
          window.location.href = '/cart';
        })
        .catch(function () {
          addBtn.disabled = false;
          addBtn.classList.remove('is-busy');
          addBtn.textContent = addBtn.dataset.labelComplete;
          announce("That didn't go through. Please try again.");
        });
    });
  }

  restore();

  /* A bundle restored from a previous pageview is not a completion the visitor just performed,
     so it must not scroll the page out from under them on load. */
  wasComplete = chosen.length === SIZE;

  render();
})();
